import { useCallback, useMemo, useRef, useState } from 'react';
import { parse } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { ReleaseType } from '@ibk/shared-types';
import { combineValidationResults, validateInOptions, validatePositiveNumber, validateRequired, } from '@ibk/shared-utils';
import { useSimulateVoucherContractRechargeMutation } from '../services/benefitApi';
import { VoucherContractRechargeType, VoucherDiscountType, VoucherRechargeDayType, } from '../types';
import { isActiveStatus } from '../utils';
import { mapRechargeDetailToFormData } from '../utils/mapRechargeDetailToFormData';
import { canReuseRechargeSimulation } from './rechargeSimulationEquivalence';
export function useRechargeSimulation() {
    const lastSuccessfulSimulationRef = useRef(null);
    const [formData, setFormData] = useState({
        competenceDate: new Date(),
        isIndividualDiscount: true,
        rechargeType: useLocation().state?.rechargeType ??
            VoucherContractRechargeType.RECURRENT_RECHARGE,
    });
    const [simulateRecharge, { data: simulationResult, isLoading, isError, error, reset }] = useSimulateVoucherContractRechargeMutation();
    const simulate = useCallback(async (voucherContractId, params) => {
        lastSuccessfulSimulationRef.current = null;
        try {
            const result = await simulateRecharge({
                voucherContractId,
                body: params,
            }).unwrap();
            if (typeof result.result === 'string') {
                console.error('API returned error:', result.result);
                return null;
            }
            const record = {
                voucherContractId,
                request: params,
                response: result.result,
            };
            if (canReuseRechargeSimulation(voucherContractId, params, record)) {
                lastSuccessfulSimulationRef.current = record;
            }
            return result.result;
        }
        catch (err) {
            console.error('Error simulating recharge:', err);
            return null;
        }
    }, [simulateRecharge]);
    const ensureSimulation = useCallback(async (voucherContractId, params) => {
        const record = lastSuccessfulSimulationRef.current;
        if (canReuseRechargeSimulation(voucherContractId, params, record)) {
            return record?.response ?? null;
        }
        return simulate(voucherContractId, params);
    }, [simulate]);
    const calculateTotalAmount = useCallback((beneficiaries) => {
        return beneficiaries.reduce((total, beneficiary) => total + (beneficiary.value ?? 0), 0);
    }, []);
    const validateSimulationParams = useCallback((params) => {
        const validationResults = [];
        if (!params.vouchers || params.vouchers.length === 0) {
            return ['At least one voucher is required'];
        }
        params.vouchers.forEach((voucher, voucherIndex) => {
            const voucherLabel = `Voucher ${voucherIndex + 1}`;
            validationResults.push(validateRequired(voucher.voucherTypeId ?? '', `${voucherLabel}: Voucher Type ID`));
            validationResults.push(validateRequired(voucher.discountType, `${voucherLabel}: Discount Type`));
            validationResults.push(validateRequired(voucher.rechargeDayType, `${voucherLabel}: Recharge Day Type`));
            validationResults.push(validateRequired(voucher.releaseType, `${voucherLabel}: Release Type`));
            if (voucher.discountValue !== undefined) {
                validationResults.push(validatePositiveNumber(voucher.discountValue, `${voucherLabel}: Discount Value`));
            }
            if (voucher.discountType) {
                validationResults.push(validateInOptions(voucher.discountType, Object.values(VoucherDiscountType), `${voucherLabel}: Discount Type`));
            }
            if (voucher.rechargeDayType) {
                validationResults.push(validateInOptions(voucher.rechargeDayType, Object.values(VoucherRechargeDayType), `${voucherLabel}: Recharge Day Type`));
            }
            if (voucher.releaseType) {
                validationResults.push(validateInOptions(voucher.releaseType, Object.values(ReleaseType), `${voucherLabel}: Release Type`));
            }
            if (voucher.beneficiaries && voucher.beneficiaries.length > 0) {
                voucher.beneficiaries.forEach((beneficiary, beneficiaryIndex) => {
                    const beneficiaryLabel = `${voucherLabel} - Beneficiary ${beneficiaryIndex + 1}`;
                    validationResults.push(validateRequired(beneficiary.beneficiaryId, `${beneficiaryLabel}: ID`));
                    if (beneficiary.value !== undefined) {
                        validationResults.push(validatePositiveNumber(beneficiary.value, `${beneficiaryLabel}: Value`));
                    }
                    if (beneficiary.daysQuantity !== undefined) {
                        validationResults.push(validatePositiveNumber(beneficiary.daysQuantity, `${beneficiaryLabel}: Days Quantity`));
                    }
                });
            }
            if (voucher.rechargeDayType === VoucherRechargeDayType.FIXED_DAY) {
                validationResults.push(validateRequired(voucher.rechargeDay?.toString() ?? '', `${voucherLabel}: Recharge day`));
                if (voucher.rechargeDay && (voucher.rechargeDay < 1 || voucher.rechargeDay > 31)) {
                    validationResults.push({
                        isValid: false,
                        errors: [`${voucherLabel}: Recharge day must be between 1 and 31`],
                    });
                }
            }
            if (voucher.releaseType === ReleaseType.DAILY && voucher.amountDaily !== undefined) {
                validationResults.push(validatePositiveNumber(voucher.amountDaily, `${voucherLabel}: Amount Daily`));
            }
        });
        const combinedResult = combineValidationResults(...validationResults);
        return combinedResult.errors;
    }, []);
    const invalidateSimulationRecord = useCallback(() => {
        lastSuccessfulSimulationRef.current = null;
    }, []);
    const clearSimulation = useCallback(() => {
        invalidateSimulationRecord();
        reset();
    }, [invalidateSimulationRecord, reset]);
    const result = useMemo(() => {
        if (!simulationResult)
            return null;
        if (typeof simulationResult.result === 'string') {
            return null;
        }
        return simulationResult.result;
    }, [simulationResult]);
    const selectCompetence = useCallback((date) => {
        setFormData(prev => {
            const prevYm = prev.competenceDate
                ? `${prev.competenceDate.getFullYear()}-${String(prev.competenceDate.getMonth() + 1).padStart(2, '0')}`
                : '';
            const nextYm = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : '';
            const competenceMonthChanged = prevYm !== nextYm;
            return {
                ...prev,
                competenceDate: date,
                creditDate: undefined,
                ...(competenceMonthChanged ? { businessDays: undefined } : {}),
            };
        });
    }, [setFormData]);
    const selectVoucherTypeId = useCallback((value) => {
        setFormData(prev => ({
            ...prev,
            voucherTypeId: value,
            creditDate: undefined,
            beneficiaries: [],
            selectedBeneficiaries: 0,
            repeatRechargeExcludedBeneficiaryDiff: undefined,
        }));
    }, [setFormData]);
    const selectVoucherType = useCallback((value) => {
        setFormData(prev => ({
            ...prev,
            voucherType: value,
            creditDate: undefined,
            beneficiaries: [],
            selectedBeneficiaries: 0,
            repeatRechargeExcludedBeneficiaryDiff: undefined,
        }));
    }, [setFormData]);
    const selectPayrollDeduction = useCallback((value) => {
        setFormData(prev => ({ ...prev, payrollDeduction: value }));
    }, [setFormData]);
    const selectDiscountType = useCallback((value) => {
        setFormData(prev => ({ ...prev, discountType: value }));
    }, [setFormData]);
    const selectDiscountValue = useCallback((value) => {
        setFormData(prev => ({ ...prev, discountValue: value }));
    }, [setFormData]);
    const selectIsIndividualDiscount = useCallback((value) => {
        setFormData(prev => ({ ...prev, isIndividualDiscount: value }));
    }, [setFormData]);
    const selectCreditDate = useCallback((date) => {
        setFormData(prev => ({ ...prev, creditDate: date }));
    }, [setFormData]);
    const setBeneficiariesData = useCallback((data) => {
        setFormData(prev => ({ ...prev, ...data }));
    }, [setFormData]);
    const populateFormData = useCallback((simulation) => {
        if (simulation.vouchers?.length !== 1)
            return;
        const voucher = simulation.vouchers[0];
        const activeBeneficiaries = voucher.beneficiaries?.filter(beneficiary => isActiveStatus(beneficiary.statusInProduct));
        const creditDateParsed = parse(`${voucher.creditDate}`, 'yyyy-MM-dd', new Date());
        setFormData(prev => {
            const previousById = new Map((prev.selectedBeneficiariesList ?? []).map(s => [s.beneficiaryId, s]));
            const hasPrefill = previousById.size > 0;
            const mergeField = (sim, prevVal) => (sim !== 0 ? sim : (prevVal ?? sim));
            const beneficiariesMerged = hasPrefill && voucher.beneficiaries
                ? voucher.beneficiaries.map(b => {
                    const prevSel = previousById.get(b.id);
                    if (!prevSel) {
                        return b;
                    }
                    const simVal = b.productValue ?? 0;
                    const simDays = b.productDaysQuantity ?? 0;
                    const simDisc = b.productDiscountValue ?? 0;
                    return {
                        ...b,
                        productValue: mergeField(simVal, prevSel.value),
                        productDaysQuantity: mergeField(simDays, prevSel.daysQuantity),
                        productDiscountValue: mergeField(simDisc, prevSel.discountValue),
                    };
                })
                : voucher.beneficiaries;
            const selectedBeneficiariesList = hasPrefill
                ? activeBeneficiaries?.map(beneficiary => {
                    const prevSel = previousById.get(beneficiary.id);
                    const simVal = beneficiary.productValue ?? 0;
                    const simDays = beneficiary.productDaysQuantity ?? 0;
                    const simDisc = beneficiary.productDiscountValue ?? 0;
                    if (!prevSel) {
                        return {
                            beneficiaryId: beneficiary.id,
                            value: simVal,
                            daysQuantity: simDays,
                            discountValue: simDisc,
                        };
                    }
                    return {
                        beneficiaryId: beneficiary.id,
                        value: mergeField(simVal, prevSel.value),
                        daysQuantity: mergeField(simDays, prevSel.daysQuantity),
                        discountValue: mergeField(simDisc, prevSel.discountValue),
                    };
                })
                : activeBeneficiaries?.map(beneficiary => ({
                    beneficiaryId: beneficiary.id,
                    value: beneficiary.productValue ?? 0,
                    daysQuantity: beneficiary.productDaysQuantity ?? 0,
                    discountValue: beneficiary.productDiscountValue ?? 0,
                }));
            return {
                ...prev,
                creditDate: creditDateParsed,
                releaseType: prev.releaseType ?? voucher.releaseType,
                beneficiaries: beneficiariesMerged,
                selectedBeneficiariesList,
                selectedBeneficiaries: activeBeneficiaries?.length ?? 0,
            };
        });
    }, []);
    const setInitialFormDataFromRechargeDetail = useCallback((detail, product) => {
        setFormData(prev => ({ ...prev, ...mapRechargeDetailToFormData(detail, product) }));
    }, []);
    const selectRechargeType = useCallback((value) => {
        setFormData(prev => ({ ...prev, rechargeType: value }));
    }, [setFormData]);
    const setRechargeIdentification = useCallback((value) => {
        setFormData(prev => ({ ...prev, rechargeIdentification: value }));
    }, [setFormData]);
    const handlers = useMemo(() => ({
        selectCompetence,
        selectVoucherTypeId,
        selectVoucherType,
        selectPayrollDeduction,
        selectDiscountType,
        selectDiscountValue,
        selectIsIndividualDiscount,
        selectCreditDate,
        setBeneficiariesData,
        selectRechargeType,
        setRechargeIdentification,
    }), [
        selectCompetence,
        selectVoucherTypeId,
        selectVoucherType,
        selectPayrollDeduction,
        selectDiscountType,
        selectDiscountValue,
        selectIsIndividualDiscount,
        selectCreditDate,
        setBeneficiariesData,
        selectRechargeType,
        setRechargeIdentification,
    ]);
    return {
        formData,
        simulate,
        ensureSimulation,
        simulationResult: result,
        isLoading,
        isError,
        error,
        calculateTotalAmount,
        validateSimulationParams,
        invalidateSimulationRecord,
        clearSimulation,
        populateFormData,
        setInitialFormDataFromRechargeDetail,
        handlers,
    };
}
